import { ApolloClient, HttpLink, InMemoryCache } from "@apollo/client/core";
import { MissingRefreshTokenError } from "@auth0/auth0-spa-js";
import { signal } from "@preact-signals/safe-react";
import * as Sentry from "@sentry/react";
import { PARAMS } from "@src/common/constants";
import config from "@src/config";
import { isAbortError, isOfflineNetworkError } from "@src/utils/error-handling";
import { auth0Client } from "./auth0-provider-with-navigate";
import {
	loginInteractively,
	shouldUseDesktopBrowserLogin,
} from "./desktop-browser-login";
import {
	guestWebSocketTokenQuery,
	guestWebSocketTokenQuerySchema,
} from "./queries/guest-websocket-token";

export const accessTokenSignal = signal<string | null>(null);
export const shareKeySignal = signal<string | null>(null);
export const websocketGuestWhiteboardIdSignal = signal<string | null>(null);

export function isAuthenticated() {
	return accessTokenSignal.value !== null;
}

export function isUnauthenticatedError(error: unknown): boolean {
	if (error instanceof MissingRefreshTokenError) {
		return true;
	}

	if (typeof error !== "object" || error === null) {
		return false;
	}

	const err = error as { error?: unknown; message?: unknown };
	const errorCode = typeof err.error === "string" ? err.error : null;
	if (
		errorCode === "login_required" ||
		errorCode === "consent_required" ||
		errorCode === "missing_refresh_token" ||
		errorCode === "invalid_grant"
	) {
		return true;
	}

	const message = typeof err.message === "string" ? err.message : null;
	if (message) {
		if (
			message.includes("Consent required") ||
			message.includes("Missing Refresh Token") ||
			message.includes("login_required") ||
			message.includes("Invalid refresh token")
		) {
			return true;
		}
	}

	return false;
}

const PROACTIVE_REFRESH_BUFFER_MS = 2 * 60 * 1000;
const MAX_TIMEOUT_MS = 2_147_483_647;

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let lifecycleListenersRegistered = false;

export function getJwtExpirationMs(token: string): number | null {
	const parts = token.split(".");
	if (parts.length !== 3 || !parts[1]) {
		return null;
	}

	try {
		const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
		const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
		const payload = JSON.parse(atob(padded)) as { exp?: unknown };
		if (typeof payload.exp === "number" && Number.isFinite(payload.exp)) {
			return payload.exp * 1000;
		}
	} catch {
		// Non-JWT or malformed token
	}

	return null;
}

export function clearProactiveTokenRefresh() {
	if (proactiveRefreshTimer !== null) {
		clearTimeout(proactiveRefreshTimer);
		proactiveRefreshTimer = null;
	}
}

function registerTokenLifecycleListeners() {
	if (
		lifecycleListenersRegistered ||
		typeof window === "undefined" ||
		typeof document === "undefined"
	) {
		return;
	}
	lifecycleListenersRegistered = true;

	const checkAndRefreshIfExpiring = () => {
		const currentToken = accessTokenSignal.value;
		if (!currentToken) {
			return;
		}
		const expMs = getJwtExpirationMs(currentToken);
		if (expMs !== null && Date.now() >= expMs - PROACTIVE_REFRESH_BUFFER_MS) {
			void getAccessToken({ forceRefresh: true }).catch(() => {
				// Handled inside getAccessToken
			});
		}
	};

	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") {
			checkAndRefreshIfExpiring();
		}
	});
	window.addEventListener("online", checkAndRefreshIfExpiring);
}

export function scheduleProactiveTokenRefresh(token: string | null) {
	clearProactiveTokenRefresh();
	if (!token) {
		return;
	}

	registerTokenLifecycleListeners();

	const expMs = getJwtExpirationMs(token);
	if (expMs === null) {
		return;
	}

	const delayMs = Math.min(
		Math.max(0, expMs - Date.now() - PROACTIVE_REFRESH_BUFFER_MS),
		MAX_TIMEOUT_MS,
	);

	proactiveRefreshTimer = setTimeout(() => {
		proactiveRefreshTimer = null;
		void getAccessToken({ forceRefresh: true }).catch(() => {
			// Handled inside getAccessToken
		});
	}, delayMs);
}

// Function to setup asset authentication with HTTP-only cookie
let lastAccessToken: string | null | undefined = undefined;
let lastShareKey: string | null | undefined = undefined;
let cachedGuestWebSocketToken: string | null = null;
let cachedGuestWebSocketTokenExpiresAt = 0;
let cachedGuestWebSocketTokenKey: string | null = null;
let inFlightTokenRequest: {
	promise: Promise<string | null>;
	forceRefresh: boolean;
	id: number;
} | null = null;
let nextTokenRequestId = 0;

// Standalone client so this module never imports getApiClient() / authLink — avoids
// circular dependency while fetching a guest WS token before the main client runs.
const guestWebSocketTokenClient = new ApolloClient({
	link: new HttpLink({
		uri: config.graphqlUrl,
		credentials: "include",
	}),
	cache: new InMemoryCache(),
});

export const setupAssetAuth = async (
	accessToken: string | null,
	shareKey: string | null,
) => {
	// Validate that at least one authentication method is provided
	if (!accessToken && !shareKey) {
		throw new Error(
			"To fetch asset cookie specify accessToken and/or shareKey.",
		);
	}

	// Only proceed if accessToken or shareKey has changed since last call
	if (accessToken === lastAccessToken && shareKey === lastShareKey) {
		return; // No change, skip fetch
	}

	lastAccessToken = accessToken;
	lastShareKey = shareKey;

	try {
		// Prepare URL and add shareKey as query param if available
		let authUrl = `${config.restApiUrl}/v1/assets/auth`;
		if (shareKey) {
			authUrl += `?shareKey=${encodeURIComponent(shareKey)}`;
		}

		// Prepare headers
		const headers: Record<string, string> = {};
		if (accessToken) {
			headers.Authorization = `Bearer ${accessToken}`;
		}

		// Call the asset auth endpoint to set the HTTP-only cookie
		await fetch(authUrl, {
			method: "POST",
			headers,
			credentials: "include", // Important for cookies
		});
	} catch (err) {
		// Soft-fail: callers continue; assets may 401 until a later retry.
		lastAccessToken = null;
		lastShareKey = null;
		console.warn("Failed to set asset authentication cookie:", err);
		if (!isAbortError(err) && !isOfflineNetworkError(err)) {
			Sentry.captureException(err);
		}
	}
};

export function getShareKey(searchParams?: string) {
	const params = new URLSearchParams(searchParams ?? window.location.search);
	shareKeySignal.value = params.get(PARAMS.shareKey) ?? null;
	return shareKeySignal.value;
}

export interface GetAccessTokenOptions {
	forceRefresh?: boolean;
}

export async function handleUnauthorizedSession(): Promise<void> {
	accessTokenSignal.value = null;
	clearProactiveTokenRefresh();
	getShareKey();

	if (!shareKeySignal.value) {
		// Desktop: never auto-open the system browser from API calls —
		// Sign in must be user-initiated via DesktopSignedOutScreen.
		if (!shouldUseDesktopBrowserLogin()) {
			await loginInteractively({
				appState: {
					returnTo: `${window.location.pathname}${window.location.search}`,
				},
			});
		}
	}
}

function isActiveTokenRequest(requestId: number): boolean {
	return inFlightTokenRequest?.id === requestId;
}

async function performGetAccessToken(
	options: GetAccessTokenOptions | undefined,
	requestId: number,
): Promise<string | null> {
	getShareKey();

	try {
		const token = options?.forceRefresh
			? await auth0Client.getTokenSilently({ cacheMode: "off" })
			: await auth0Client.getTokenSilently();
		// A newer forceRefresh may have superseded this request while we
		// awaited Auth0 — do not overwrite a fresher token with a cached one.
		if (!isActiveTokenRequest(requestId)) {
			return accessTokenSignal.value;
		}
		accessTokenSignal.value = token;
		scheduleProactiveTokenRefresh(token);
	} catch (error) {
		if (!isActiveTokenRequest(requestId)) {
			throw error;
		}
		if (isUnauthenticatedError(error)) {
			await handleUnauthorizedSession();
			if (!shareKeySignal.value) {
				throw error;
			}
		} else if (!shareKeySignal.value) {
			throw error;
		}
	}

	if (!isActiveTokenRequest(requestId)) {
		return accessTokenSignal.value;
	}

	// setupAssetAuth is a network call to our backend — if it fails the user
	// is still authenticated, don't redirect to Auth0.
	try {
		await setupAssetAuth(accessTokenSignal.value, shareKeySignal.value);
	} catch {
		// Already logged + reported to Sentry inside setupAssetAuth
	}

	return accessTokenSignal.value;
}

export async function getAccessToken(
	options?: GetAccessTokenOptions,
): Promise<string | null> {
	const forceRefresh = Boolean(options?.forceRefresh);

	if (
		inFlightTokenRequest &&
		(!forceRefresh || inFlightTokenRequest.forceRefresh)
	) {
		return inFlightTokenRequest.promise;
	}

	const requestId = ++nextTokenRequestId;
	const promise = performGetAccessToken(options, requestId).finally(() => {
		if (inFlightTokenRequest?.id === requestId) {
			inFlightTokenRequest = null;
		}
	});

	inFlightTokenRequest = { promise, forceRefresh, id: requestId };
	return promise;
}

export async function getGuestWebSocketToken() {
	shareKeySignal.value = getShareKey();

	const shareKey = shareKeySignal.value;
	const whiteboardId = websocketGuestWhiteboardIdSignal.value;

	if (!shareKey || !whiteboardId) {
		return null;
	}

	const cacheKey = `${whiteboardId}:${shareKey}`;
	if (
		cachedGuestWebSocketToken &&
		cachedGuestWebSocketTokenKey === cacheKey &&
		Date.now() < cachedGuestWebSocketTokenExpiresAt - 10_000
	) {
		return cachedGuestWebSocketToken;
	}

	const response = await guestWebSocketTokenClient.query({
		query: guestWebSocketTokenQuery,
		variables: {
			whiteboardId,
			shareKey,
		},
		fetchPolicy: "no-cache",
	});
	const payload = guestWebSocketTokenQuerySchema.parse(
		response.data,
	).guestWebSocketToken;

	cachedGuestWebSocketToken = payload.token;
	cachedGuestWebSocketTokenKey = cacheKey;
	cachedGuestWebSocketTokenExpiresAt = Date.parse(payload.expiresAt);

	return payload.token;
}
